import React, { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import api from "../api/axios";

const CANVAS = { width: 1360, height: 780 };
const SNAPSHOT_KEY = "medgen_molecular_studio_avatar_snapshots_v1";

const NODE_THEME = {
  element: { color: "#0f766e", fill: "#ecfdf5", label: "Element" },
  custom_element: { color: "#be123c", fill: "#fff1f2", label: "Custom Element / Variant" },
  compound: { color: "#7c3aed", fill: "#f5f3ff", label: "Compound" },
  biomolecule: { color: "#7c3aed", fill: "#f5f3ff", label: "Biomolecule" },
  protein: { color: "#ea580c", fill: "#fff7ed", label: "Protein / Target" },
  medicine: { color: "#059669", fill: "#ecfdf5", label: "Medicine" },
  vaccine: { color: "#be185d", fill: "#fdf2f8", label: "Vaccine" },
  patient: { color: "#475569", fill: "#f8fafc", label: "Avatar Context" },
};

const BOND_TYPES = {
  single: { label: "Single", stroke: "#2563eb", width: 2.1, dash: "", order: 1 },
  double: { label: "Double", stroke: "#0f766e", width: 3.0, dash: "", order: 2 },
  triple: { label: "Triple", stroke: "#7c3aed", width: 3.8, dash: "", order: 3 },
  ionic: { label: "Ionic", stroke: "#f97316", width: 2.6, dash: "7 5", order: 1 },
  hydrogen: { label: "Hydrogen", stroke: "#0891b2", width: 2.1, dash: "4 5", order: 0.5 },
  receptor_binding: { label: "Receptor", stroke: "#db2777", width: 2.6, dash: "10 5", order: 0.5 },
  inhibition: { label: "Inhibit", stroke: "#dc2626", width: 2.8, dash: "5 4", order: 0.5 },
  activation: { label: "Activate", stroke: "#16a34a", width: 2.8, dash: "5 4", order: 0.5 },
};

const PERIODIC = {
  H: { symbol: "H", name: "Hydrogen", protons: 1, neutrons: 0, electrons: 1, atomic_number: 1, color: "#94a3b8" },
  He: { symbol: "He", name: "Helium", protons: 2, neutrons: 2, electrons: 2, atomic_number: 2, color: "#64748b" },
  Li: { symbol: "Li", name: "Lithium", protons: 3, neutrons: 4, electrons: 3, atomic_number: 3, color: "#a855f7" },
  Be: { symbol: "Be", name: "Beryllium", protons: 4, neutrons: 5, electrons: 4, atomic_number: 4, color: "#84cc16" },
  B: { symbol: "B", name: "Boron", protons: 5, neutrons: 6, electrons: 5, atomic_number: 5, color: "#14b8a6" },
  C: { symbol: "C", name: "Carbon", protons: 6, neutrons: 6, electrons: 6, atomic_number: 6, color: "#111827" },
  N: { symbol: "N", name: "Nitrogen", protons: 7, neutrons: 7, electrons: 7, atomic_number: 7, color: "#2563eb" },
  O: { symbol: "O", name: "Oxygen", protons: 8, neutrons: 8, electrons: 8, atomic_number: 8, color: "#dc2626" },
  F: { symbol: "F", name: "Fluorine", protons: 9, neutrons: 10, electrons: 9, atomic_number: 9, color: "#16a34a" },
  Ne: { symbol: "Ne", name: "Neon", protons: 10, neutrons: 10, electrons: 10, atomic_number: 10, color: "#06b6d4" },
  Na: { symbol: "Na", name: "Sodium", protons: 11, neutrons: 12, electrons: 11, atomic_number: 11, color: "#6366f1" },
  Mg: { symbol: "Mg", name: "Magnesium", protons: 12, neutrons: 12, electrons: 12, atomic_number: 12, color: "#14b8a6" },
  Al: { symbol: "Al", name: "Aluminium", protons: 13, neutrons: 14, electrons: 13, atomic_number: 13, color: "#64748b" },
  Si: { symbol: "Si", name: "Silicon", protons: 14, neutrons: 14, electrons: 14, atomic_number: 14, color: "#475569" },
  P: { symbol: "P", name: "Phosphorus", protons: 15, neutrons: 16, electrons: 15, atomic_number: 15, color: "#f97316" },
  S: { symbol: "S", name: "Sulfur", protons: 16, neutrons: 16, electrons: 16, atomic_number: 16, color: "#eab308" },
  Cl: { symbol: "Cl", name: "Chlorine", protons: 17, neutrons: 18, electrons: 17, atomic_number: 17, color: "#16a34a" },
  Ar: { symbol: "Ar", name: "Argon", protons: 18, neutrons: 22, electrons: 18, atomic_number: 18, color: "#0ea5e9" },
  K: { symbol: "K", name: "Potassium", protons: 19, neutrons: 20, electrons: 19, atomic_number: 19, color: "#8b5cf6" },
  Ca: { symbol: "Ca", name: "Calcium", protons: 20, neutrons: 20, electrons: 20, atomic_number: 20, color: "#22c55e" },
  Fe: { symbol: "Fe", name: "Iron", protons: 26, neutrons: 30, electrons: 26, atomic_number: 26, color: "#ea580c" },
  I: { symbol: "I", name: "Iodine", protons: 53, neutrons: 74, electrons: 53, atomic_number: 53, color: "#7c3aed" },
};

const PERIODIC_BY_NUMBER = Object.fromEntries(
  Object.values(PERIODIC).map((item) => [item.atomic_number, item])
);

const DEFAULT_ELEMENTS = Object.values(PERIODIC);

const studioCss = `
.mol-studio-shell {
  --ms-bg: #f6faff;
  --ms-card: rgba(255,255,255,0.96);
  --ms-border: #cddcec;
  --ms-text: #07111f;
  --ms-muted: #64748b;
  --ms-blue: #1f7bc1;
  --ms-cyan: #18b7ff;
  --ms-dark: #03060c;
  --ms-navy: #081525;
  width: 100%;
  color: var(--ms-text);
}

.mol-studio-shell * {
  box-sizing: border-box;
}

.mol-studio-headerbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 160px;
  gap: 18px;
  align-items: stretch;
  padding: 22px;
  border-radius: 22px;
  background:
    radial-gradient(circle at 100% 0%, rgba(24,183,255,0.10), transparent 34%),
    rgba(255,255,255,0.97);
  border: 1px solid var(--ms-border);
  box-shadow: 0 16px 44px rgba(7,17,31,0.06);
}

.mol-studio-headerbar p,
.mol-card-title,
.mol-picker-head p,
.mol-context-section small,
.mol-data-head span {
  margin: 0 0 8px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.17em;
  text-transform: uppercase;
}

.mol-studio-headerbar h2 {
  margin: 0;
  font-size: clamp(25px, 3vw, 38px);
  line-height: 1.06;
  letter-spacing: -0.05em;
  font-weight: 950;
}

.mol-studio-headerbar span {
  display: block;
  margin-top: 8px;
  color: var(--ms-muted);
  line-height: 1.55;
  font-size: 13px;
}

.mol-studio-counters {
  padding: 18px;
  border-radius: 18px;
  color: white;
  background:
    radial-gradient(circle at 100% 0%, rgba(24,183,255,0.20), transparent 36%),
    linear-gradient(145deg, var(--ms-dark), var(--ms-navy));
  display: grid;
  align-content: center;
  gap: 4px;
}

.mol-studio-counters strong {
  color: #9ff7ff;
  font-size: 28px;
  line-height: 1;
}

.mol-studio-counters span {
  color: #a9bdd3;
  font-size: 11px;
  font-weight: 850;
  margin: 0 0 8px;
}

.mol-workbench {
  display: grid;
  grid-template-columns: 215px minmax(0, 1fr) 240px;
  gap: 14px;
  margin-top: 14px;
  align-items: start;
}

.mol-sidebar {
  display: grid;
  gap: 12px;
}

.mol-card,
.mol-data-panel {
  padding: 14px;
  border-radius: 18px;
  background: var(--ms-card);
  border: 1px solid var(--ms-border);
  box-shadow: 0 12px 34px rgba(7,17,31,0.055);
}

.mol-card h3 {
  margin: 0;
  color: var(--ms-text);
  font-size: 16px;
  font-weight: 950;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.mol-card p {
  margin: 8px 0 0;
  color: var(--ms-muted);
  font-size: 12px;
  line-height: 1.5;
}

.mol-type-chip {
  display: inline-flex;
  width: fit-content;
  margin-top: 10px;
  padding: 5px 9px;
  border-radius: 999px;
  background: #eef6ff;
  color: #0b4e84;
  border: 1px solid rgba(31,123,193,0.16);
  font-size: 10px;
  font-weight: 950;
}

.mol-type-chip.custom_element {
  color: #be123c;
  background: #fff1f2;
  border-color: rgba(190,18,60,0.18);
}

.mol-atom-editor-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}

.mol-atom-editor-grid label,
.mol-formula-label {
  display: grid;
  gap: 5px;
  color: #44576e;
  font-size: 10px;
  font-weight: 950;
}

.mol-atom-editor-grid input,
.mol-formula-input,
.mol-picker-search,
.mol-save-input {
  width: 100%;
  min-height: 34px;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid var(--ms-border);
  outline: none;
  background: #fff;
  color: var(--ms-text);
  font-size: 12px;
  font-weight: 800;
}

.mol-help-text {
  color: var(--ms-muted);
  font-size: 11px;
  line-height: 1.45;
}

.mol-variant-warning {
  margin-top: 9px;
  padding: 10px;
  border-radius: 12px;
  background: #fff1f2;
  border: 1px solid rgba(190,18,60,0.16);
  color: #9f1239;
  font-size: 11px;
  font-weight: 850;
  line-height: 1.45;
}

.mol-formula-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 7px;
}

.mol-formula-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 7px;
  margin-top: 8px;
}

.mol-formula-actions button,
.mol-formula-apply,
.mol-small-btn,
.mol-actions-card button,
.mol-stage-toolbar button,
.mol-data-tabs button,
.mol-context-menu button,
.mol-picker-tabs button,
.mol-snapshot-actions button,
.mol-table-action {
  min-height: 34px;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid var(--ms-border);
  background: #f8fbff;
  color: #102033;
  cursor: pointer;
  font-size: 11px;
  font-weight: 950;
  transition: 0.16s ease;
}

.mol-formula-actions button:hover,
.mol-formula-apply:hover,
.mol-small-btn:hover,
.mol-actions-card button:hover,
.mol-stage-toolbar button:hover,
.mol-context-menu button:hover,
.mol-picker-tabs button:hover,
.mol-snapshot-actions button:hover,
.mol-table-action:hover {
  transform: translateY(-1px);
  border-color: rgba(31,123,193,0.45);
}

.mol-formula-apply,
.mol-primary-btn,
.mol-actions-card button.primary,
.mol-context-menu button.primary,
.mol-data-tabs button.active,
.mol-picker-tabs button.active {
  color: white;
  border-color: transparent;
  background: linear-gradient(135deg, var(--ms-blue), #0f5f9d);
  box-shadow: 0 10px 22px rgba(31,123,193,0.16);
}

.mol-dark-btn,
.mol-actions-card button.dark {
  color: white;
  border-color: transparent;
  background: linear-gradient(135deg, #03060c, #081525);
}

.mol-danger,
.mol-context-menu button.danger,
.mol-table-action.danger {
  color: #991b1b;
  background: rgba(239,68,68,0.08);
  border-color: rgba(239,68,68,0.18);
}

.mol-bond-list {
  display: grid;
  gap: 7px;
}

.mol-bond-list button {
  min-height: 32px;
  padding: 7px 9px;
  border-radius: 10px;
  border: 1px solid var(--ms-border);
  background: #f8fbff;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 900;
}

.mol-bond-list button.active {
  color: white;
  background: #0f766e;
  border-color: transparent;
}

.mol-bond-list i {
  width: 12px;
  height: 4px;
  border-radius: 999px;
}

.mol-main-stage {
  min-width: 0;
  padding: 10px;
  border-radius: 18px;
  background: var(--ms-card);
  border: 1px solid var(--ms-border);
  box-shadow: 0 16px 44px rgba(7,17,31,0.06);
}

.mol-stage-toolbar {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  padding: 4px 4px 10px;
}

.mol-toolgroup {
  display: flex;
  gap: 7px;
  flex-wrap: wrap;
}

.mol-icon-button {
  min-width: 34px;
}

.mol-status-row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  margin-bottom: 10px;
  border-radius: 14px;
  background: #f7fbff;
  border: 1px solid #d9e8f6;
}

.mol-status-row strong {
  color: var(--ms-text);
  font-size: 12px;
  font-weight: 950;
}

.mol-status-row span {
  color: var(--ms-muted);
  font-size: 11px;
  font-weight: 850;
  text-align: right;
}

.mol-svg {
  width: 100%;
  height: 620px;
  display: block;
  border-radius: 18px;
  background: #fff;
  border: 1px solid #d9e8f6;
  touch-action: none;
}

.mol-node {
  cursor: grab;
  user-select: none;
}

.mol-node:active {
  cursor: grabbing;
}

.mol-node-title {
  fill: #07111f;
  font-size: 13px;
  font-weight: 950;
}

.mol-node-subtitle {
  fill: #64748b;
  font-size: 10.5px;
  font-weight: 800;
}

.mol-node.selected rect:first-child {
  filter: drop-shadow(0 0 11px rgba(245,158,11,0.38));
}

.mol-node.link-source rect:first-child {
  stroke: #ec4899;
  stroke-width: 3;
  filter: drop-shadow(0 0 11px rgba(236,72,153,0.34));
}

.mol-edge {
  cursor: pointer;
}

.mol-edge-line {
  pointer-events: none;
}

.mol-edge-dot {
  pointer-events: none;
}

.mol-edge-label {
  fill: #475569;
  font-size: 10px;
  font-weight: 900;
  pointer-events: none;
  paint-order: stroke;
  stroke: #fff;
  stroke-width: 4;
}

.mol-actions-card {
  display: grid;
  gap: 8px;
}

.mol-dark-card {
  color: white;
  background:
    radial-gradient(circle at 100% 0%, rgba(24,183,255,0.18), transparent 34%),
    linear-gradient(145deg, #03060c, #081525);
  border-color: rgba(24,183,255,0.18);
}

.mol-dark-card .mol-card-title {
  color: #9fdcff;
}

.mol-dark-card p {
  color: #a9bdd3;
}

.mol-medgen-card pre {
  min-height: 160px;
  max-height: 340px;
  overflow: auto;
  padding: 12px;
  margin: 8px 0 0;
  border-radius: 14px;
  color: #dbeafe;
  background: #07111f;
  border: 1px solid rgba(24,183,255,0.16);
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 11px;
  line-height: 1.5;
}

.mol-live-badge {
  display: inline-flex;
  margin-left: 7px;
  padding: 3px 7px;
  border-radius: 999px;
  background: rgba(16,185,129,0.12);
  color: #059669;
  font-size: 9px;
  font-weight: 950;
  letter-spacing: 0;
  text-transform: uppercase;
}

.mol-medgen-thinking {
  margin-top: 8px;
  padding: 10px;
  border-radius: 12px;
  color: #0b4e84;
  background: rgba(31,123,193,0.10);
  border: 1px solid rgba(31,123,193,0.16);
  font-size: 11px;
  font-weight: 850;
  line-height: 1.45;
}

.mol-stream-events {
  display: grid;
  gap: 6px;
  margin-top: 8px;
}

.mol-stream-event {
  padding: 8px;
  border-radius: 10px;
  background: #f7fbff;
  border: 1px solid #d9e8f6;
  color: var(--ms-muted);
  font-size: 10px;
  line-height: 1.4;
}

.mol-stream-event.error {
  color: #991b1b;
  background: #fff1f2;
  border-color: rgba(239,68,68,0.2);
}

.mol-stream-event.success,
.mol-stream-event.complete {
  color: #065f46;
  background: #ecfdf5;
  border-color: rgba(16,185,129,0.2);
}

.mol-data-panel {
  margin-top: 14px;
}

.mol-data-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 12px;
}

.mol-data-head strong {
  display: block;
  color: var(--ms-text);
  font-size: 16px;
  font-weight: 950;
}

.mol-data-head span {
  display: block;
  margin: 6px 0 0;
  letter-spacing: 0;
  text-transform: none;
  color: var(--ms-muted);
  font-size: 12px;
}

.mol-data-tabs {
  display: flex;
  gap: 7px;
}

.mol-table-wrap {
  width: 100%;
  overflow-x: auto;
}

.mol-data-table {
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
}

.mol-data-table th,
.mol-data-table td {
  padding: 10px;
  border-bottom: 1px solid #d9e8f6;
  text-align: left;
  vertical-align: top;
  font-size: 12px;
}

.mol-data-table th {
  color: #125a94;
  font-size: 10px;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.mol-data-table td {
  color: #334155;
}

.mol-linked-list {
  display: grid;
  gap: 7px;
}

.mol-linked-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 7px;
  align-items: center;
  padding: 8px;
  border-radius: 12px;
  background: #f7fbff;
  border: 1px solid #d9e8f6;
}

.mol-linked-row strong {
  font-size: 12px;
}

.mol-linked-row span {
  display: block;
  margin-top: 3px;
  color: var(--ms-muted);
  font-size: 10px;
  font-weight: 850;
}

.mol-context-menu {
  position: fixed;
  z-index: 99999;
  width: 250px;
  padding: 12px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid var(--ms-border);
  box-shadow: 0 24px 70px rgba(3,6,12,0.22);
}

.mol-context-head {
  padding-bottom: 10px;
  border-bottom: 1px solid #d9e8f6;
  margin-bottom: 10px;
}

.mol-context-head strong {
  display: block;
  color: var(--ms-text);
  font-size: 14px;
  font-weight: 950;
}

.mol-context-head span {
  display: block;
  margin-top: 4px;
  color: var(--ms-muted);
  font-size: 11px;
  font-weight: 850;
}

.mol-context-section {
  display: grid;
  gap: 7px;
  margin-top: 10px;
}

.mol-context-section.row {
  grid-template-columns: 1fr 1fr;
}

.mol-context-bonds {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}

.mol-context-bonds button.active {
  color: white;
  background: #0f766e;
  border-color: transparent;
}

.mol-picker-backdrop {
  position: fixed;
  inset: 0;
  z-index: 99990;
  padding: 24px;
  display: grid;
  place-items: center;
  background: rgba(3,6,12,0.62);
  backdrop-filter: blur(14px);
}

.mol-picker-modal {
  width: min(980px, 96vw);
  max-height: 90vh;
  overflow: auto;
  padding: 20px;
  border-radius: 24px;
  background: #fff;
  border: 1px solid var(--ms-border);
  box-shadow: 0 30px 90px rgba(0,0,0,0.28);
}

.mol-picker-head {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
}

.mol-picker-head h2 {
  margin: 0;
  color: var(--ms-text);
  font-size: 28px;
  line-height: 1.1;
  letter-spacing: -0.045em;
}

.mol-picker-head span {
  display: block;
  margin-top: 7px;
  color: var(--ms-muted);
  line-height: 1.5;
}

.mol-picker-head button {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  border: 1px solid var(--ms-border);
  background: #f8fbff;
  cursor: pointer;
  font-size: 22px;
  font-weight: 950;
}

.mol-picker-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 16px 0 10px;
}

.mol-picker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(185px, 1fr));
  gap: 10px;
  margin-top: 12px;
}

.mol-picker-grid button {
  min-height: 112px;
  padding: 13px;
  border-radius: 14px;
  border: 1px solid var(--ms-border);
  background: #fff;
  text-align: left;
  cursor: pointer;
  transition: 0.16s ease;
}

.mol-picker-grid button:hover {
  transform: translateY(-2px);
  border-color: rgba(31,123,193,0.45);
  box-shadow: 0 14px 30px rgba(31,123,193,0.10);
}

.mol-picker-grid strong {
  display: block;
  color: var(--ms-text);
  font-size: 13px;
  font-weight: 950;
}

.mol-picker-grid span {
  display: block;
  margin-top: 6px;
  color: var(--ms-muted);
  font-size: 11px;
  line-height: 1.35;
}

.mol-picker-grid small {
  display: inline-flex;
  margin-top: 9px;
  padding: 4px 7px;
  border-radius: 999px;
  color: #0b4e84;
  background: rgba(31,123,193,0.10);
  border: 1px solid rgba(31,123,193,0.16);
  font-size: 9px;
  font-weight: 950;
}

.mol-picker-empty {
  padding: 20px;
  border-radius: 16px;
  color: var(--ms-muted);
  background: #f7fbff;
  border: 1px solid var(--ms-border);
  font-weight: 850;
}

.mol-avatar-snapshot-card {
  display: grid;
  gap: 10px;
}

.mol-snapshot-list {
  display: grid;
  gap: 8px;
  max-height: 240px;
  overflow: auto;
}

.mol-snapshot-item {
  padding: 10px;
  border-radius: 14px;
  border: 1px solid #d9e8f6;
  background: #f7fbff;
}

.mol-snapshot-item strong {
  display: block;
  color: var(--ms-text);
  font-size: 12px;
  font-weight: 950;
}

.mol-snapshot-item span {
  display: block;
  margin-top: 4px;
  color: var(--ms-muted);
  font-size: 10px;
  line-height: 1.35;
}

.mol-snapshot-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 7px;
  margin-top: 8px;
}

@media (max-width: 1260px) {
  .mol-workbench {
    grid-template-columns: 1fr;
  }

  .mol-left,
  .mol-right {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .mol-svg {
    height: 560px;
  }
}

@media (max-width: 760px) {
  .mol-studio-headerbar,
  .mol-left,
  .mol-right,
  .mol-data-head {
    grid-template-columns: 1fr;
  }

  .mol-studio-headerbar,
  .mol-card,
  .mol-main-stage,
  .mol-data-panel {
    border-radius: 16px;
  }

  .mol-stage-toolbar,
  .mol-status-row,
  .mol-data-head {
    flex-direction: column;
  }

  .mol-toolgroup,
  .mol-data-tabs,
  .mol-picker-tabs {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .mol-toolgroup button,
  .mol-data-tabs button,
  .mol-picker-tabs button,
  .mol-actions-card button {
    width: 100%;
  }

  .mol-svg {
    height: 520px;
  }

  .mol-picker-modal {
    padding: 16px;
  }

  .mol-picker-grid {
    grid-template-columns: 1fr;
  }
}
`;

function uid(prefix = "id") {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function safeList(value) {
  return Array.isArray(value) ? value : [];
}

function cleanText(value, fallback = "") {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  return text || fallback;
}

function truncate(value, length = 34) {
  const text = cleanText(value);
  return text.length > length ? `${text.slice(0, length - 1)}…` : text;
}

function toNumber(value, fallback = null) {
  if (value === null || value === undefined || value === "") return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toInt(value, fallback = null) {
  const number = toNumber(value, fallback);
  return number === null || number === undefined ? fallback : Math.round(number);
}

function normalizeSymbol(value) {
  const raw = cleanText(value);
  if (!raw) return "";
  if (raw.length === 1) return raw.toUpperCase();
  return `${raw[0].toUpperCase()}${raw.slice(1).toLowerCase()}`;
}

function atomDefaults(input) {
  const bySymbol = PERIODIC[normalizeSymbol(input?.symbol || input?.element_symbol || input?.atom_symbol)];
  const byNumber = PERIODIC_BY_NUMBER[toInt(input?.atomic_number ?? input?.protons, null)];
  return bySymbol || byNumber || null;
}

function normalizeElementItem(item = {}) {
  const defaults = atomDefaults(item) || {};
  const symbol = normalizeSymbol(
    item.symbol || item.element_symbol || item.atom_symbol || defaults.symbol || item.name
  );

  const protons = toInt(item.protons ?? item.atomic_number ?? defaults.protons ?? defaults.atomic_number, null);
  const electrons = toInt(item.electrons ?? protons ?? defaults.electrons, null);
  const neutrons = toInt(item.neutrons ?? defaults.neutrons, null);

  return {
    ...item,
    type: item.type || "element",
    symbol,
    element_symbol: symbol,
    name: item.name || item.element_name || defaults.name || symbol,
    atomic_number: toInt(item.atomic_number ?? protons, null),
    protons,
    neutrons,
    electrons,
    color: item.color || item.atom_color || defaults.color || "#0f766e",
  };
}

function makeCustomElementVariant(baseNode, patch = {}) {
  const previous = normalizeElementItem(baseNode || {});
  const nextProtons = toInt(patch.protons ?? patch.atomic_number ?? baseNode?.protons, previous.protons);
  const nextNeutrons = toInt(patch.neutrons ?? baseNode?.neutrons, previous.neutrons);
  const nextElectrons = toInt(patch.electrons ?? baseNode?.electrons, previous.electrons);
  const knownByProtons = PERIODIC_BY_NUMBER[nextProtons];

  const baseSymbol = knownByProtons?.symbol || previous.symbol || `E${nextProtons || "X"}`;
  const massNumber =
    Number.isFinite(Number(nextProtons)) && Number.isFinite(Number(nextNeutrons))
      ? Number(nextProtons) + Number(nextNeutrons)
      : null;

  const isSameStableKnown =
    knownByProtons &&
    previous.symbol === knownByProtons.symbol &&
    nextProtons === knownByProtons.protons &&
    nextNeutrons === knownByProtons.neutrons &&
    nextElectrons === knownByProtons.electrons;

  if (isSameStableKnown) {
    return {
      type: "element",
      label: knownByProtons.symbol,
      symbol: knownByProtons.symbol,
      name: knownByProtons.name,
      protons: knownByProtons.protons,
      neutrons: knownByProtons.neutrons,
      electrons: knownByProtons.electrons,
      atomic_number: knownByProtons.atomic_number,
      color: knownByProtons.color,
      is_custom_variant: false,
      custom_reason: "",
      subLabel: `${knownByProtons.name} · p${knownByProtons.protons} n${knownByProtons.neutrons} e${knownByProtons.electrons}`,
    };
  }

  const ionText =
    Number.isFinite(Number(nextProtons)) && Number.isFinite(Number(nextElectrons))
      ? nextElectrons < nextProtons
        ? `+${nextProtons - nextElectrons}`
        : nextElectrons > nextProtons
          ? `-${nextElectrons - nextProtons}`
          : "neutral"
      : "charge unknown";

  const label = massNumber ? `${baseSymbol}-${massNumber}` : `${baseSymbol}-X`;
  const name = knownByProtons
    ? `${knownByProtons.name} variant`
    : `Element-X Z${nextProtons || "?"}`;

  return {
    type: "custom_element",
    label,
    symbol: baseSymbol,
    name,
    protons: nextProtons,
    neutrons: nextNeutrons,
    electrons: nextElectrons,
    atomic_number: nextProtons,
    color: "#be123c",
    is_custom_variant: true,
    custom_reason: `Custom isotope/ion variant: p${nextProtons ?? "?"} n${nextNeutrons ?? "?"} e${nextElectrons ?? "?"} · ${ionText}`,
    subLabel: `${name} · p${nextProtons ?? "?"} n${nextNeutrons ?? "?"} e${nextElectrons ?? "?"}`,
  };
}

function labelOf(item, type) {
  if (!item) return "Node";
  if (type === "element") return normalizeElementItem(item).symbol || "Element";
  if (type === "protein") return item.symbol || item.name || item.target_name || "Protein";
  if (type === "medicine" || type === "vaccine") return item.name || item.generic_name || "Candidate";
  return item.name || item.display_formula || item.formula || item.molecular_formula || "Compound";
}

function subLabelOf(item, type) {
  if (!item) return "";

  if (type === "element") {
    const atom = normalizeElementItem(item);
    return `${atom.name || atom.symbol} · p${atom.protons ?? "?"} n${atom.neutrons ?? "?"} e${atom.electrons ?? "?"}`;
  }

  if (type === "protein") {
    return item.primary_organ || item.system || item.protein_type || item.target_type || "Target / pathway";
  }

  if (type === "medicine" || type === "vaccine") {
    return item.generic_name || item.category || item.drug_class || item.mechanism || "Medicine hypothesis";
  }

  return item.display_formula || item.formula || item.molecular_formula || item.compound_type || item.category || "Compound library item";
}

function nodeWidth(node) {
  if (node.type === "element" || node.type === "custom_element") return 98;
  if (node.type === "compound" || node.type === "biomolecule") {
    return Math.max(170, Math.min(265, 105 + cleanText(node.label).length * 7));
  }
  return Math.max(135, Math.min(230, 92 + cleanText(node.label).length * 7));
}

function nodeHeight(node) {
  if (node.type === "element" || node.type === "custom_element") return 68;
  if (node.type === "compound" || node.type === "biomolecule") return 80;
  return 68;
}

function getNodeColor(node) {
  if (node.type === "element") return node.color || PERIODIC[node.symbol]?.color || "#0f766e";
  if (node.type === "custom_element") return "#be123c";
  return NODE_THEME[node.type]?.color || "#0f766e";
}

function getNodeFill(node) {
  if (node.type === "element" || node.type === "custom_element") return `${getNodeColor(node)}14`;
  return NODE_THEME[node.type]?.fill || "#ecfdf5";
}

function edgeKey(a, b) {
  return [a, b].sort().join("--");
}

function formulaFromNodes(nodes) {
  const counts = {};
  const compoundLabels = [];

  nodes.forEach((node) => {
    if (node.type === "element" || node.type === "custom_element") {
      const symbol = node.symbol || node.element_symbol || node.label || "X";
      counts[symbol] = (counts[symbol] || 0) + 1;
    }

    if (node.type === "compound" || node.type === "biomolecule") {
      const formula = node.meta?.formula || node.meta?.display_formula || node.symbol || node.label;
      if (formula) compoundLabels.push(String(formula).replace(/[₀-₉]/g, ""));
    }
  });

  const atomFormula = Object.entries(counts)
    .map(([symbol, count]) => `${symbol}${count > 1 ? count : ""}`)
    .join("");

  if (atomFormula && compoundLabels.length) return `${compoundLabels.join("+")}+${atomFormula}`;
  if (compoundLabels.length) return compoundLabels.join("+");
  return atomFormula;
}

function parseFormulaToAtomRows(formula) {
  const value = cleanText(formula).replace(/\s+/g, "");
  if (!value) return [];

  const matches = [...value.matchAll(/([A-Z][a-z]?)(\d*)/g)];

  return matches.map((match, index) => {
    const atom = normalizeElementItem({ symbol: match[1] });
    return {
      ...atom,
      id: `${atom.symbol}-${index + 1}`,
      count: Math.max(1, Number(match[2] || 1)),
    };
  });
}

function expandAtomRows(atomRows) {
  const result = [];

  safeList(atomRows).forEach((row, rowIndex) => {
    const atom = normalizeElementItem(row);
    const count = Math.max(1, Number(row.count || row.quantity || row.atom_count || row.stoichiometry || 1));

    for (let i = 0; i < count; i += 1) {
      result.push({
        ...atom,
        id: `${atom.symbol}-${rowIndex + 1}-${i + 1}-${Math.random().toString(36).slice(2, 6)}`,
        sourceAtomId: String(row.id || row.atom_id || `${atom.symbol}-${rowIndex + 1}`),
        label: atom.symbol,
        subLabel: `${atom.name} · p${atom.protons ?? "?"} n${atom.neutrons ?? "?"} e${atom.electrons ?? "?"}`,
        raw: row,
      });
    }
  });

  return result;
}

function normalizeCompoundAtoms(item) {
  const rawAtoms =
    item?.atoms ||
    item?.compound_atoms ||
    item?.metadata_json?.atoms ||
    item?.metadata?.atoms ||
    item?.detail?.atoms ||
    [];

  if (Array.isArray(rawAtoms) && rawAtoms.length > 0) return expandAtomRows(rawAtoms);

  return expandAtomRows(
    parseFormulaToAtomRows(item?.display_formula || item?.formula || item?.molecular_formula || "")
  );
}

function layoutAtoms(atomCount, anchor) {
  const centerX = anchor?.x ?? CANVAS.width / 2;
  const centerY = anchor?.y ?? CANVAS.height / 2;

  if (atomCount <= 1) return [{ x: centerX, y: centerY }];

  if (atomCount <= 4) {
    const gap = 120;
    return Array.from({ length: atomCount }, (_, index) => ({
      x: centerX + (index - (atomCount - 1) / 2) * gap,
      y: centerY + (index % 2 === 0 ? -18 : 18),
    }));
  }

  const radius = Math.max(120, Math.min(300, 34 * atomCount));

  return Array.from({ length: atomCount }, (_, index) => {
    const angle = (Math.PI * 2 * index) / atomCount - Math.PI / 2;
    return {
      x: centerX + Math.cos(angle) * radius,
      y: centerY + Math.sin(angle) * radius,
    };
  });
}

function normalizeBackendBaseUrl(value) {
  const raw = cleanText(value);
  if (!raw) return "http://127.0.0.1:8000";
  return raw.replace(/\/+$/, "").replace(/\/api$/i, "");
}

function buildBackendUrl(path, explicitBaseUrl = "") {
  const rawBase = cleanText(
    explicitBaseUrl ||
      api?.defaults?.baseURL ||
      "http://127.0.0.1:8000"
  );

  const base = normalizeBackendBaseUrl(rawBase);
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${base}${normalizedPath}`;
}

function getAuthToken(explicitToken) {
  if (explicitToken) return explicitToken;

  const keys = ["token", "access_token", "authToken", "medai_token", "jwt"];

  for (const key of keys) {
    const value = window.localStorage?.getItem(key) || window.sessionStorage?.getItem(key);
    if (value) return value.replace(/^Bearer\s+/i, "");
  }

  try {
    const user = JSON.parse(window.localStorage?.getItem("user") || "null");
    return user?.token || user?.access_token || null;
  } catch {
    return null;
  }
}

async function readSseResponse(response, handlers = {}) {
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Request failed: HTTP ${response.status}`);
  }

  if (!response.body) throw new Error("Streaming response body is not available.");

  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  let buffer = "";
  let activeEvent = "message";
  let finalText = "";

  function handleChunk(chunk) {
    const lines = chunk
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    for (const line of lines) {
      if (line.startsWith("event:")) {
        activeEvent = line.replace("event:", "").trim();
        continue;
      }

      if (!line.startsWith("data:")) continue;

      const raw = line.replace("data:", "").trim();
      if (!raw || raw === "[DONE]") continue;

      let data;
      try {
        data = JSON.parse(raw);
      } catch {
        data = { text: raw };
      }

      if (activeEvent === "start") {
        handlers.onStart?.(data);
      } else if (activeEvent === "token") {
        const token = data.text || data.token || data.content || "";
        finalText += token;
        handlers.onToken?.(token, data);
      } else if (activeEvent === "progress") {
        handlers.onProgress?.(data.message || "Working...", data);
      } else if (activeEvent === "compound_saved") {
        handlers.onCompoundSaved?.(data);
      } else if (activeEvent === "simulation_created") {
        handlers.onSimulationCreated?.(data);
      } else if (activeEvent === "complete") {
        if (data.text && !finalText) finalText = data.text;
        handlers.onComplete?.(data);
      } else if (activeEvent === "error") {
        const message = data.message || "Streaming API returned an error.";
        const detail = data.detail || data.error || "";
        handlers.onError?.(data);
        throw new Error(detail ? `${message}\n\n${detail}` : message);
      }
    }
  }

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const chunks = buffer.split("\n\n");
    buffer = chunks.pop() || "";

    for (const chunk of chunks) {
      handleChunk(chunk);
    }
  }

  if (buffer.trim()) handleChunk(buffer);

  return finalText.trim();
}

function compactPatientContext(patient, session, sessionId, patientResearchContext) {
  const patientSource = patient || patientResearchContext?.patient || {};
  const sessionSource = session || patientResearchContext?.session || {};

  return {
    patient_id: patientSource?.id || patientResearchContext?.patient_id || null,
    name: patientSource?.name || patientResearchContext?.patient_name || null,
    age: patientSource?.age ?? patientResearchContext?.age ?? null,
    gender: patientSource?.gender || patientSource?.sex || patientResearchContext?.gender || null,
    sex: patientSource?.sex || patientSource?.gender || patientResearchContext?.sex || null,
    bmi: patientResearchContext?.bmi || patientSource?.bmi || null,
    known_conditions:
      patientResearchContext?.known_conditions ||
      patientSource?.known_conditions ||
      patientSource?.conditions ||
      [],
    current_medications:
      patientResearchContext?.current_medications ||
      patientSource?.current_medications ||
      patientSource?.medications ||
      [],
    notes:
      patientResearchContext?.notes ||
      patientSource?.notes ||
      sessionSource?.summary ||
      "Research-only digital avatar simulation.",
    session_summary: sessionSource?.summary || patientResearchContext?.session_summary || null,
    session_id: sessionSource?.id || sessionId || patientResearchContext?.session_id || null,
  };
}

function makeStreamEvent(type, message) {
  return {
    id: uid("stream-event"),
    type,
    message,
    ts: new Date().toLocaleTimeString(),
  };
}

function readSnapshots() {
  try {
    const raw = window.localStorage.getItem(SNAPSHOT_KEY);
    const rows = JSON.parse(raw || "[]");
    return Array.isArray(rows) ? rows : [];
  } catch {
    return [];
  }
}

function writeSnapshots(rows) {
  window.localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(rows));
}

export default function MolecularResearchStudio({
  elements = [],
  compounds = [],
  proteins = [],
  medicines = [],
  patient = null,
  session = null,
  sessionId = null,
  patientResearchContext = null,
  apiBaseUrl = "",
  authToken = null,
  onAddToAvatarQueue,
  onSaveDesignedCompound,
  onRunAvatarSimulation,
  onImpactResult,
}) {
  const svgRef = useRef(null);
  const viewportRef = useRef(null);
  const transformRef = useRef(d3.zoomIdentity.translate(20, 8).scale(0.93));
  const dragRef = useRef(null);
  const streamStartedAtRef = useRef(null);
  const thinkingTimerRef = useRef(null);

  const hydratedElements = useMemo(() => {
    const source = safeList(elements).length ? elements : DEFAULT_ELEMENTS;
    return source.map(normalizeElementItem);
  }, [elements]);

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [linkSourceId, setLinkSourceId] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [edgeContextMenu, setEdgeContextMenu] = useState(null);
  const [picker, setPicker] = useState(null);
  const [pickerSearch, setPickerSearch] = useState("");
  const [bondType, setBondType] = useState("single");
  const [tableMode, setTableMode] = useState("nodes");
  const [rotation, setRotation] = useState(0);
  const [medgenText, setMedgenText] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [savedCompoundId, setSavedCompoundId] = useState(null);
  const [simulationId, setSimulationId] = useState(null);
  const [streamEvents, setStreamEvents] = useState([]);
  const [status, setStatus] = useState(
    "Studio ready. Add atoms, compounds, targets, then stream Qwen/Medgen reasoning."
  );
  const [snapshots, setSnapshots] = useState(() => readSnapshots());
  const [snapshotName, setSnapshotName] = useState("");

  const selectedNode = useMemo(
    () => nodes.find((node) => node.id === selectedNodeId) || null,
    [nodes, selectedNodeId]
  );

  const selectedNodeLinks = useMemo(() => {
    if (!selectedNodeId) return [];

    return edges
      .filter((edge) => edge.source === selectedNodeId || edge.target === selectedNodeId)
      .map((edge) => {
        const otherId = edge.source === selectedNodeId ? edge.target : edge.source;
        const otherNode = nodes.find((node) => node.id === otherId);

        return {
          edge,
          otherNode,
        };
      })
      .filter((row) => row.otherNode);
  }, [edges, nodes, selectedNodeId]);

  const graphStats = useMemo(
    () => ({
      nodes: nodes.length,
      edges: edges.length,
      elements: nodes.filter((n) => n.type === "element" || n.type === "custom_element").length,
      customElements: nodes.filter((n) => n.type === "custom_element" || n.is_custom_variant).length,
      compounds: nodes.filter((n) => ["compound", "biomolecule"].includes(n.type)).length,
      proteins: nodes.filter((n) => n.type === "protein").length,
      medicines: nodes.filter((n) => ["medicine", "vaccine"].includes(n.type)).length,
      formula: formulaFromNodes(nodes) || "GraphCandidate",
    }),
    [nodes, edges]
  );

  const designedPayload = useMemo(() => {
    const elementNodes = nodes.filter((node) => node.type === "element" || node.type === "custom_element");
    const compoundNodes = nodes.filter((node) => ["compound", "biomolecule"].includes(node.type));
    const atomLabelByNodeId = new Map();

    const atoms = elementNodes.map((node, index) => {
      const normalized = normalizeElementItem(node);
      const symbol = node.symbol || normalized.symbol || node.label || "X";
      const atomLabel = node.atom_label || `${symbol}${index + 1}`;

      atomLabelByNodeId.set(node.id, atomLabel);

      return {
        id: atomLabel,
        node_id: node.id,
        atom_label: atomLabel,
        label: symbol,
        name: node.name || normalized.name || PERIODIC[symbol]?.name || symbol,
        symbol,
        element_symbol: symbol,
        x: Number(Number(node.x || 0).toFixed(2)),
        y: Number(Number(node.y || 0).toFixed(2)),
        z: Number(Number(node.z || 0).toFixed(2)),
        charge: toNumber(node.charge, 0),
        protons: toInt(node.protons ?? normalized.protons, null),
        neutrons: toInt(node.neutrons ?? normalized.neutrons, null),
        electrons: toInt(node.electrons ?? normalized.electrons, null),
        atomic_number: toInt(node.atomic_number ?? node.protons ?? normalized.atomic_number, null),
        is_custom_variant: Boolean(node.is_custom_variant || node.type === "custom_element"),
        custom_reason: node.custom_reason || null,
      };
    });

    const bonds = edges.map((edge, index) => {
      const sourceLabel = atomLabelByNodeId.get(edge.source) || edge.source;
      const targetLabel = atomLabelByNodeId.get(edge.target) || edge.target;
      const nextType = edge.bondType || "single";

      return {
        id: `bond-${index + 1}`,
        source: sourceLabel,
        target: targetLabel,
        source_node_id: edge.source,
        target_node_id: edge.target,
        from_atom_label: sourceLabel,
        to_atom_label: targetLabel,
        type: nextType,
        bond_type: nextType,
        bond_order: BOND_TYPES[nextType]?.order || 1,
      };
    });

    const compoundBlocks = compoundNodes.map((node) => ({
      id: node.id,
      name: node.label,
      formula: node.meta?.formula || node.meta?.display_formula || node.symbol || node.label,
      display_formula: node.meta?.display_formula || node.meta?.formula || node.symbol || node.label,
      compound_type: node.type,
      context: node.subLabel,
      x: Number(Number(node.x || 0).toFixed(2)),
      y: Number(Number(node.y || 0).toFixed(2)),
      metadata: {
        source: "molecular_research_studio",
        original_id: node.meta?.id || null,
        research_only: true,
      },
    }));

    const targets = nodes
      .filter((node) => node.type === "protein")
      .map((node) => ({
        id: node.id,
        label: node.label,
        name: node.label,
        context: node.subLabel,
        metadata: {
          source: "molecular_research_studio",
          research_only: true,
        },
      }));

    const selectedMedicines = nodes
      .filter((node) => ["medicine", "vaccine"].includes(node.type))
      .map((node) => ({
        id: node.id,
        name: node.label,
        context: node.subLabel,
        metadata: {
          source: "molecular_research_studio",
          research_only: true,
        },
      }));

    const compactContext = compactPatientContext(patient, session, sessionId, patientResearchContext);

    return {
      name: `Studio Candidate ${graphStats.formula}`,
      formula: graphStats.formula,
      display_formula: graphStats.formula,
      compound_type: "designed_compound",
      description: "Designed in Molecular Research Studio. Research-only digital-avatar simulation candidate.",
      notes: "Testing Qwen chemistry stream through MedAIAssist backend.",
      patient_id: patient?.id || patientResearchContext?.patient_id || null,
      session_id: session?.id || sessionId || patientResearchContext?.session_id || null,
      patient_context: compactContext,
      atoms,
      bonds,
      compounds: compoundBlocks,
      compound_blocks: compoundBlocks,
      proteins: targets,
      targets,
      medicines: selectedMedicines,
      graph_nodes: nodes,
      graph_edges: edges,
      question:
        "Assess this molecule/graph-designed compound for chemical plausibility, stability, bonding concerns, possible biological relevance, and digital-avatar organ implications. Keep it research-only.",
      max_tokens: 700,
      metadata: {
        source: "molecular_research_studio",
        research_only: true,
        frontend_snapshot_ready: true,
        generated_at: new Date().toISOString(),
      },
    };
  }, [nodes, edges, graphStats.formula, patient, session, sessionId, patientResearchContext]);

  useEffect(() => {
    if (!svgRef.current || !viewportRef.current) return;

    const svg = d3.select(svgRef.current);
    const viewport = d3.select(viewportRef.current);

    const zoom = d3.zoom().scaleExtent([0.25, 4]).on("zoom", (event) => {
      transformRef.current = event.transform;
      viewport.attr("transform", event.transform.toString());
    });

    svg.call(zoom);
    svg.call(zoom.transform, transformRef.current);

    return () => svg.on(".zoom", null);
  }, []);

  useEffect(() => {
    const onMove = (event) => {
      if (!dragRef.current || !svgRef.current) return;

      event.preventDefault?.();

      const point = clientToGraphPoint(event.clientX, event.clientY);
      const { nodeId, offsetX, offsetY } = dragRef.current;

      setNodes((prev) =>
        prev.map((node) =>
          node.id === nodeId
            ? {
                ...node,
                x: point.x - offsetX,
                y: point.y - offsetY,
              }
            : node
        )
      );
    };

    const onUp = () => {
      dragRef.current = null;
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);

    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (thinkingTimerRef.current) clearTimeout(thinkingTimerRef.current);
    };
  }, []);

  function pushStreamEvent(type, message) {
    setStreamEvents((prev) => [...prev.slice(-7), makeStreamEvent(type, message)]);
  }

  function clientToGraphPoint(clientX, clientY) {
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();

    pt.x = clientX;
    pt.y = clientY;

    const screen = pt.matrixTransform(svg.getScreenCTM().inverse());
    const [x, y] = transformRef.current.invert([screen.x, screen.y]);

    return { x, y };
  }

  function startNodeDrag(event, node) {
    if (event.button !== 0) return;

    event.preventDefault();
    event.stopPropagation();
    event.nativeEvent?.stopImmediatePropagation?.();

    try {
      event.currentTarget?.setPointerCapture?.(event.pointerId);
    } catch {
      // Safari fallback handled by window pointer listeners.
    }

    const point = clientToGraphPoint(event.clientX, event.clientY);

    dragRef.current = {
      nodeId: node.id,
      pointerId: event.pointerId,
      offsetX: point.x - Number(node.x || 0),
      offsetY: point.y - Number(node.y || 0),
    };

    setSelectedNodeId(node.id);
    setStatus(`${node.label} selected. Drag to reposition.`);
  }

  function createOrUpdateEdge(sourceId, targetId, type = bondType) {
    if (!sourceId || !targetId || sourceId === targetId) return;

    const key = edgeKey(sourceId, targetId);

    setEdges((prev) => {
      const existing = prev.find((edge) => edgeKey(edge.source, edge.target) === key);

      if (existing) {
        return prev.map((edge) =>
          edge.id === existing.id
            ? {
                ...edge,
                bondType: type,
                note: `${type} link`,
              }
            : edge
        );
      }

      return [
        ...prev,
        {
          id: uid("edge"),
          source: sourceId,
          target: targetId,
          bondType: type,
          note: `${type} link`,
        },
      ];
    });

    setStatus(`${BOND_TYPES[type]?.label || type} link created.`);
  }

  function handleNodeClick(event, node) {
    event.stopPropagation();

    setSelectedNodeId(node.id);
    setContextMenu(null);
    setEdgeContextMenu(null);

    if (linkSourceId && linkSourceId !== node.id) {
      createOrUpdateEdge(linkSourceId, node.id, bondType);
      setLinkSourceId(null);
      return;
    }

    if (!linkSourceId) {
      setLinkSourceId(node.id);
      setStatus(`${node.label} is link source. Click another node to create ${bondType} link.`);
    }
  }

  function openNodeContextMenu(event, node) {
    event.preventDefault();
    event.stopPropagation();

    setSelectedNodeId(node.id);
    setEdgeContextMenu(null);
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      nodeId: node.id,
    });
  }

  function openEdgeContextMenu(event, edge) {
    event.preventDefault();
    event.stopPropagation();

    setContextMenu(null);
    setSelectedNodeId(null);
    setEdgeContextMenu({
      x: event.clientX,
      y: event.clientY,
      edgeId: edge.id,
    });
  }

  function addElementNode(item, anchor = null, extra = {}) {
    const atom = normalizeElementItem(item);
    const id = uid(`atom-${atom.symbol || "X"}`);

    const node = {
      id,
      type: "element",
      label: atom.symbol || "X",
      subLabel: `${atom.name || atom.symbol} · p${atom.protons ?? "?"} n${atom.neutrons ?? "?"} e${atom.electrons ?? "?"}`,
      symbol: atom.symbol,
      name: atom.name,
      protons: atom.protons,
      neutrons: atom.neutrons,
      electrons: atom.electrons,
      atomic_number: atom.atomic_number,
      charge: atom.charge ?? 0,
      color: atom.color,
      x: (anchor?.x ?? CANVAS.width / 2) + (extra.dx ?? 0),
      y: (anchor?.y ?? CANVAS.height / 2) + (extra.dy ?? 0),
      z: extra.z ?? 0,
      sourceCompoundId: extra.sourceCompoundId || null,
      is_custom_variant: false,
      custom_reason: "",
      meta: atom,
    };

    setNodes((prev) => [...prev, node]);

    return node;
  }

  function addCompoundBlock(item, anchorNodeId = null) {
    const anchor =
      nodes.find((node) => node.id === anchorNodeId) ||
      selectedNode || {
        x: CANVAS.width / 2,
        y: CANVAS.height / 2,
      };

    const nodeType = cleanText(item?.compound_type).toLowerCase().includes("bio") ? "biomolecule" : "compound";
    const id = uid(nodeType);

    const newNode = {
      id,
      type: nodeType,
      label: labelOf(item, "compound"),
      subLabel: subLabelOf(item, "compound"),
      symbol: item?.formula || item?.display_formula || item?.molecular_formula || item?.name || "",
      x: Number(anchor.x || CANVAS.width / 2) + (anchorNodeId ? 190 : 0),
      y: Number(anchor.y || CANVAS.height / 2) + (anchorNodeId ? 40 : 0),
      meta: item || {},
    };

    setNodes((prev) => [...prev, newNode]);

    if (anchorNodeId && anchorNodeId !== id) {
      createOrUpdateEdge(anchorNodeId, id, bondType);
    }

    setSelectedNodeId(id);
    setPicker(null);
    setPickerSearch("");
    setStatus(`${newNode.label} added as a compound block. Right-click it to expand into atoms.`);
  }

  function addCompoundAsAtoms(item, anchorNodeId = null, removeCompoundNodeId = null) {
    const anchor =
      nodes.find((node) => node.id === anchorNodeId) ||
      selectedNode || {
        x: CANVAS.width / 2,
        y: CANVAS.height / 2,
      };

    const atomRows = normalizeCompoundAtoms(item);

    if (!atomRows.length) {
      setStatus("Compound has no atom/formula data to expand.");
      return;
    }

    const positions = layoutAtoms(atomRows.length, anchor);
    const sourceCompoundId = item?.id || uid("compound-source");

    const newNodes = atomRows.map((atom, index) => {
      const normalized = normalizeElementItem(atom);

      return {
        id: uid(`atom-${normalized.symbol || "X"}`),
        type: "element",
        label: normalized.symbol || "X",
        subLabel: `${normalized.name || normalized.symbol} · p${normalized.protons ?? "?"} n${normalized.neutrons ?? "?"} e${normalized.electrons ?? "?"}`,
        symbol: normalized.symbol,
        name: normalized.name,
        protons: normalized.protons,
        neutrons: normalized.neutrons,
        electrons: normalized.electrons,
        atomic_number: normalized.atomic_number,
        charge: normalized.charge ?? 0,
        color: normalized.color,
        x: positions[index].x,
        y: positions[index].y,
        z: 0,
        sourceCompoundId,
        is_custom_variant: false,
        custom_reason: "",
        meta: {
          ...normalized,
          source_compound: item,
        },
      };
    });

    const explicitBonds = safeList(
      item?.bonds || item?.compound_bonds || item?.metadata_json?.bonds || item?.metadata?.bonds
    );

    const newEdges = [];

    if (explicitBonds.length) {
      explicitBonds.forEach((bond) => {
        const sourceIndex = Math.max(0, toInt(bond.source_index ?? bond.from_index ?? 0, 0));
        const targetIndex = Math.max(0, toInt(bond.target_index ?? bond.to_index ?? 1, 1));

        if (newNodes[sourceIndex] && newNodes[targetIndex] && sourceIndex !== targetIndex) {
          newEdges.push({
            id: uid("edge"),
            source: newNodes[sourceIndex].id,
            target: newNodes[targetIndex].id,
            bondType: bond.bond_type || bond.type || "single",
            note: bond.label || "compound bond",
          });
        }
      });
    } else {
      for (let index = 0; index < newNodes.length - 1; index += 1) {
        newEdges.push({
          id: uid("edge"),
          source: newNodes[index].id,
          target: newNodes[index + 1].id,
          bondType: "single",
          note: "formula-expanded bond",
        });
      }
    }

    setNodes((prev) => {
      const kept = removeCompoundNodeId ? prev.filter((node) => node.id !== removeCompoundNodeId) : prev;
      return [...kept, ...newNodes];
    });

    setEdges((prev) => {
      const kept = removeCompoundNodeId
        ? prev.filter((edge) => edge.source !== removeCompoundNodeId && edge.target !== removeCompoundNodeId)
        : prev;
      return [...kept, ...newEdges];
    });

    setSelectedNodeId(newNodes[0]?.id || null);
    setContextMenu(null);
    setPicker(null);
    setPickerSearch("");
    setStatus(`${labelOf(item, "compound")} expanded into ${newNodes.length} atom nodes.`);
  }

  function expandSelectedCompoundNode(nodeId) {
    const compoundNode = nodes.find((node) => node.id === nodeId);
    if (!compoundNode || !["compound", "biomolecule"].includes(compoundNode.type)) return;

    addCompoundAsAtoms(compoundNode.meta || compoundNode, compoundNode.id, compoundNode.id);
  }

  function addNodeFromItem(type, item, anchorNodeId = null) {
    if (type === "compound") {
      addCompoundBlock(item, anchorNodeId);
      return;
    }

    if (type === "element") {
      const anchor =
        nodes.find((node) => node.id === anchorNodeId) ||
        selectedNode || {
          x: CANVAS.width / 2,
          y: CANVAS.height / 2,
        };

      const node = addElementNode(item, anchor, {
        dx: 140 + Math.random() * 60,
        dy: (Math.random() - 0.5) * 120,
      });

      if (anchorNodeId && anchorNodeId !== node.id) {
        createOrUpdateEdge(anchorNodeId, node.id, bondType);
      }

      setSelectedNodeId(node.id);
      setPicker(null);
      setPickerSearch("");
      setStatus(`${node.label} added with p/n/e values.`);

      return;
    }

    const anchor =
      nodes.find((node) => node.id === anchorNodeId) ||
      selectedNode || {
        x: CANVAS.width / 2,
        y: CANVAS.height / 2,
      };

    const nodeType =
      type === "medicine" && cleanText(item?.compound_type).toLowerCase().includes("vaccine") ? "vaccine" : type;

    const newNode = {
      id: uid(type),
      type: nodeType,
      label: labelOf(item, type),
      subLabel: subLabelOf(item, type),
      symbol: item?.symbol || item?.formula || item?.display_formula || item?.name || "",
      x: Number(anchor.x || CANVAS.width / 2) + 160 + Math.random() * 80,
      y: Number(anchor.y || CANVAS.height / 2) + (Math.random() - 0.5) * 160,
      meta: item || {},
    };

    setNodes((prev) => [...prev, newNode]);

    if (anchorNodeId && anchorNodeId !== newNode.id) {
      createOrUpdateEdge(anchorNodeId, newNode.id, bondType);
    }

    setSelectedNodeId(newNode.id);
    setPicker(null);
    setPickerSearch("");
    setStatus(`${newNode.label} added to graph.`);
  }

  function applyFormula(formula) {
    const atomRows = parseFormulaToAtomRows(formula);

    if (!atomRows.length) {
      setStatus("Formula could not be parsed. Example: H2O, NaCl, C6H12O6.");
      return;
    }

    addCompoundAsAtoms(
      {
        id: uid("formula"),
        name: `Formula ${formula}`,
        formula,
        atoms: atomRows,
      },
      selectedNodeId
    );
  }

  function updateSelectedAtomPatch(patch) {
    if (!selectedNode || !["element", "custom_element"].includes(selectedNode.type)) return;

    setNodes((prev) =>
      prev.map((node) => {
        if (node.id !== selectedNode.id) return node;

        const variant = makeCustomElementVariant(node, patch);

        return {
          ...node,
          ...variant,
          meta: {
            ...(node.meta || {}),
            custom_variant: variant.is_custom_variant,
            custom_reason: variant.custom_reason,
            original_symbol: node.meta?.original_symbol || node.symbol,
            original_label: node.meta?.original_label || node.label,
          },
        };
      })
    );

    setStatus("Subatomic particle change applied. Variant node created in frontend graph.");
  }

  function duplicateSelectedAsVariant() {
    if (!selectedNode || !["element", "custom_element"].includes(selectedNode.type)) return;

    const variant = makeCustomElementVariant(selectedNode, {
      protons: selectedNode.protons,
      neutrons: selectedNode.neutrons,
      electrons: selectedNode.electrons,
    });

    const copy = {
      ...selectedNode,
      ...variant,
      id: uid("custom-element"),
      x: Number(selectedNode.x || 0) + 125,
      y: Number(selectedNode.y || 0) + 45,
      meta: {
        ...(selectedNode.meta || {}),
        cloned_from_node_id: selectedNode.id,
        custom_variant: true,
      },
    };

    setNodes((prev) => [...prev, copy]);
    createOrUpdateEdge(selectedNode.id, copy.id, "single");
    setSelectedNodeId(copy.id);
    setStatus(`${copy.label} variant clone created and linked.`);
  }

  function removeNode(id) {
    setNodes((prev) => prev.filter((node) => node.id !== id));
    setEdges((prev) => prev.filter((edge) => edge.source !== id && edge.target !== id));
    setSelectedNodeId(null);

    if (linkSourceId === id) setLinkSourceId(null);

    setContextMenu(null);
  }

  function removeEdge(id) {
    setEdges((prev) => prev.filter((edge) => edge.id !== id));
    setEdgeContextMenu(null);
    setStatus("Bond/link removed.");
  }

  function removeEdgeBetweenSelected(edgeId) {
    removeEdge(edgeId);
  }

  function updateEdgeBond(id, nextType) {
    setEdges((prev) =>
      prev.map((edge) =>
        edge.id === id
          ? {
              ...edge,
              bondType: nextType,
              note: `${nextType} link`,
            }
          : edge
      )
    );

    setEdgeContextMenu(null);
    setStatus(`Bond changed to ${nextType}.`);
  }

  function clearGraph() {
    setNodes([]);
    setEdges([]);
    setSelectedNodeId(null);
    setLinkSourceId(null);
    setMedgenText("");
    setSavedCompoundId(null);
    setSimulationId(null);
    setStreamEvents([]);
    setStatus("Graph cleared. Add element, compound, protein, medicine, or vaccine candidate.");
  }

  function centerGraph() {
    if (!svgRef.current || !viewportRef.current) return;

    const svg = d3.select(svgRef.current);

    const zoom = d3.zoom().scaleExtent([0.25, 4]).on("zoom", (event) => {
      transformRef.current = event.transform;
      d3.select(viewportRef.current).attr("transform", event.transform.toString());
    });

    const next = d3.zoomIdentity.translate(20, 8).scale(0.93);

    svg.transition().duration(280).call(zoom.transform, next);
    transformRef.current = next;
    d3.select(viewportRef.current).attr("transform", next.toString());

    setStatus("Canvas centered.");
  }

  function autoLayout() {
    if (!nodes.length) return;

    const centerX = CANVAS.width / 2;
    const centerY = CANVAS.height / 2;
    const radius = Math.max(160, Math.min(330, nodes.length * 24));

    setNodes((prev) =>
      prev.map((node, index) => {
        const angle = (index / prev.length) * Math.PI * 2 - Math.PI / 2;
        return {
          ...node,
          x: centerX + Math.cos(angle) * radius,
          y: centerY + Math.sin(angle) * radius,
        };
      })
    );

    setStatus("Circular layout applied. Nodes remain draggable.");
  }

  async function saveCandidate() {
    if (!designedPayload.atoms.length && !designedPayload.compound_blocks.length) {
      setStatus("Add at least one atom or compound before saving.");
      return null;
    }

    setStatus("Saving designed compound to backend...");

    try {
      if (onSaveDesignedCompound) {
        const result = await onSaveDesignedCompound(designedPayload);
        if (result?.id) setSavedCompoundId(result.id);
        setStatus("Candidate saved through parent handler.");
        return result;
      }

      const token = getAuthToken(authToken);

      const response = await fetch(buildBackendUrl("/api/research/studio/compounds", apiBaseUrl), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(designedPayload),
      });

      if (!response.ok) throw new Error(await response.text());

      const data = await response.json();
      setSavedCompoundId(data.id || data.compound_id || null);
      setStatus(`Candidate saved. Compound ID: ${data.id || data.compound_id || "saved"}`);

      return data;
    } catch (error) {
      setStatus(error?.message || "Save failed.");
      throw error;
    }
  }

  async function askMedgen() {
    if (!designedPayload.atoms.length && !designedPayload.compound_blocks.length) {
      setStatus("Add at least one atom or compound before asking Medgen/Qwen.");
      return;
    }

    setIsStreaming(true);
    setMedgenText("");
    setStreamEvents([]);
    setStatus("Starting backend simulation stream...");
    streamStartedAtRef.current = Date.now();

    pushStreamEvent("running", "Starting backend simulation stream...");

    if (thinkingTimerRef.current) clearTimeout(thinkingTimerRef.current);
    thinkingTimerRef.current = setTimeout(() => {
      if (streamStartedAtRef.current) {
        pushStreamEvent("thinking", "Qwen is thinking through the chemistry graph. Larger compounds can take a little longer.");
        setStatus("Qwen is thinking through the chemistry graph...");
      }
    }, 10000);

    try {
      const token = getAuthToken(authToken);
      const url = buildBackendUrl("/api/research/studio/simulations/stream", apiBaseUrl);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(designedPayload),
      });

      const finalText = await readSseResponse(response, {
        onStart: () => {
          pushStreamEvent("running", "Stream opened.");
        },

        onProgress: (message) => {
          setStatus(message);
          pushStreamEvent("progress", message);
        },

        onCompoundSaved: (data) => {
          if (data.compound_id) setSavedCompoundId(data.compound_id);
          const message = `Compound saved. ID: ${data.compound_id}`;
          setStatus(message);
          pushStreamEvent("success", message);
        },

        onSimulationCreated: (data) => {
          if (data.simulation_id) setSimulationId(data.simulation_id);
          const message = `Simulation created. ID: ${data.simulation_id}`;
          setStatus(message);
          pushStreamEvent("success", message);
        },

        onToken: (tokenChunk) => {
          setMedgenText((prev) => `${prev}${tokenChunk}`);
        },

        onComplete: (data) => {
          if (data.simulation_id) setSimulationId(data.simulation_id);
          if (data.compound_id) setSavedCompoundId(data.compound_id);
          onImpactResult?.(data.result || data);
          pushStreamEvent("complete", "Streaming response complete.");
        },

        onError: (data) => {
          pushStreamEvent("error", data.message || "Stream failed.");
        },
      });

      if (finalText) setMedgenText(finalText);
      setStatus("Streaming response complete.");
    } catch (error) {
      setMedgenText(error?.message || "Medgen/Qwen stream failed.");
      setStatus("Stream failed.");
      pushStreamEvent("error", error?.message || "Medgen/Qwen stream failed.");
    } finally {
      streamStartedAtRef.current = null;
      if (thinkingTimerRef.current) {
        clearTimeout(thinkingTimerRef.current);
        thinkingTimerRef.current = null;
      }
      setIsStreaming(false);
    }
  }

  function addToAvatarQueue() {
    const item = {
      compound: {
        id: savedCompoundId || `studio-${Date.now()}`,
        name: designedPayload.name,
        formula: designedPayload.formula,
        display_formula: designedPayload.formula,
        compound_type: designedPayload.compound_type,
        description: designedPayload.description,
        is_designed: true,
      },
      atoms: designedPayload.atoms,
      bonds: designedPayload.bonds,
      compound_blocks: designedPayload.compound_blocks,
      graph_nodes: nodes,
      graph_edges: edges,
      medgen_notes: medgenText,
      simulation_id: simulationId,
      patient_context: designedPayload.patient_context,
      source: "molecular_research_studio",
    };

    onAddToAvatarQueue?.(item);
    saveFrontendSnapshot("Avatar Queue Candidate", item);

    setStatus("Candidate added to Digital Avatar Test Queue and stored as local snapshot.");
  }

  function saveFrontendSnapshot(customName = "", payloadOverride = null) {
    const name =
      cleanText(customName) ||
      cleanText(snapshotName) ||
      `Avatar Snapshot ${new Date().toLocaleString()}`;

    const snapshot = {
      id: uid("avatar-snapshot"),
      name,
      created_at: new Date().toISOString(),
      patient_context: designedPayload.patient_context,
      graph_formula: graphStats.formula,
      nodes,
      edges,
      designed_payload: payloadOverride || designedPayload,
      medgen_text: medgenText,
      saved_compound_id: savedCompoundId,
      simulation_id: simulationId,
    };

    const next = [snapshot, ...snapshots].slice(0, 30);
    setSnapshots(next);
    writeSnapshots(next);
    setSnapshotName("");
    setStatus(`Frontend avatar snapshot saved: ${name}`);
    return snapshot;
  }

  function loadFrontendSnapshot(snapshot) {
    setNodes(safeList(snapshot.nodes));
    setEdges(safeList(snapshot.edges));
    setMedgenText(snapshot.medgen_text || "");
    setSavedCompoundId(snapshot.saved_compound_id || null);
    setSimulationId(snapshot.simulation_id || null);
    setSelectedNodeId(null);
    setLinkSourceId(null);
    setStatus(`Loaded frontend avatar snapshot: ${snapshot.name}`);
  }

  function deleteFrontendSnapshot(snapshotId) {
    const next = snapshots.filter((item) => item.id !== snapshotId);
    setSnapshots(next);
    writeSnapshots(next);
    setStatus("Frontend snapshot deleted.");
  }

  const viewBox = `0 0 ${CANVAS.width} ${CANVAS.height}`;

  return (
    <section
      className="mol-studio-shell"
      onClick={() => {
        setContextMenu(null);
        setEdgeContextMenu(null);
      }}
    >
      <style>{studioCss}</style>

      <div className="mol-studio-headerbar">
        <div>
          <p>Molecular Research Studio</p>
          <h2>Graph-based compound and target design</h2>
          <span>
            Select a compound as a block, connect it with atoms/proteins/medicines, remove or change links, then expand only when needed. Custom p/n/e edits become custom element variants.
          </span>
        </div>

        <div className="mol-studio-counters">
          <strong>{graphStats.nodes}</strong>
          <span>nodes</span>
          <strong>{graphStats.edges}</strong>
          <span>links</span>
        </div>
      </div>

      <div className="mol-workbench">
        <aside className="mol-sidebar mol-left">
          <div className="mol-card mol-node-card">
            <div className="mol-card-title">Selected node</div>

            {selectedNode ? (
              <>
                <h3>{selectedNode.label}</h3>
                <p>{selectedNode.subLabel || NODE_THEME[selectedNode.type]?.label || selectedNode.type}</p>
                <span className={`mol-type-chip ${selectedNode.type}`}>
                  {NODE_THEME[selectedNode.type]?.label || selectedNode.type}
                </span>

                {selectedNode.is_custom_variant && (
                  <div className="mol-variant-warning">
                    {selectedNode.custom_reason || "Custom element variant created from p/n/e changes."}
                  </div>
                )}
              </>
            ) : (
              <p>No node selected.</p>
            )}
          </div>

          {selectedNode && selectedNodeLinks.length > 0 && (
            <div className="mol-card">
              <div className="mol-card-title">Linked nodes</div>
              <div className="mol-linked-list">
                {selectedNodeLinks.map(({ edge, otherNode }) => (
                  <div key={edge.id} className="mol-linked-row">
                    <div>
                      <strong>{otherNode.label}</strong>
                      <span>{BOND_TYPES[edge.bondType]?.label || edge.bondType} · {NODE_THEME[otherNode.type]?.label || otherNode.type}</span>
                    </div>
                    <button
                      type="button"
                      className="mol-table-action danger"
                      onClick={() => removeEdgeBetweenSelected(edge.id)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedNode && ["element", "custom_element"].includes(selectedNode.type) && (
            <div className="mol-card mol-atom-editor-card">
              <div className="mol-card-title">Atom editor</div>

              <div className="mol-atom-editor-grid">
                <label>
                  Protons
                  <input
                    type="number"
                    value={selectedNode.protons ?? ""}
                    onChange={(e) =>
                      updateSelectedAtomPatch({
                        protons: e.target.value,
                        atomic_number: e.target.value,
                      })
                    }
                  />
                </label>

                <label>
                  Neutrons
                  <input
                    type="number"
                    value={selectedNode.neutrons ?? ""}
                    onChange={(e) => updateSelectedAtomPatch({ neutrons: e.target.value })}
                  />
                </label>

                <label>
                  Electrons
                  <input
                    type="number"
                    value={selectedNode.electrons ?? ""}
                    onChange={(e) => updateSelectedAtomPatch({ electrons: e.target.value })}
                  />
                </label>
              </div>

              <p className="mol-help-text">
                Changing p/n/e now converts the node into a custom isotope/ion variant instead of pretending it is the same old atom.
              </p>

              <button className="mol-small-btn" type="button" onClick={duplicateSelectedAsVariant}>
                Clone as variant
              </button>
            </div>
          )}

          <div className="mol-card">
            <div className="mol-card-title">Formula builder</div>
            <FormulaBuilder
              onApply={applyFormula}
              onWater={() => applyFormula("H2O")}
              onGlucose={() => applyFormula("C6H12O6")}
            />
          </div>

          <div className="mol-card">
            <div className="mol-card-title">Bond type</div>

            <div className="mol-bond-list">
              {Object.entries(BOND_TYPES).map(([key, item]) => (
                <button
                  key={key}
                  type="button"
                  className={bondType === key ? "active" : ""}
                  onClick={() => setBondType(key)}
                >
                  <i style={{ background: item.stroke }} /> {item.label}
                </button>
              ))}
            </div>

            <p className="mol-help-text">
              {linkSourceId
                ? `Source: ${nodes.find((n) => n.id === linkSourceId)?.label || linkSourceId}. Click target node.`
                : "Click a node to set source, then click target. Right-click bonds to remove or change type."}
            </p>

            {linkSourceId && (
              <button className="mol-small-btn" type="button" onClick={() => setLinkSourceId(null)}>
                Cancel link
              </button>
            )}
          </div>

          <div className="mol-card">
            <div className="mol-card-title">Candidate</div>
            <h3>{graphStats.formula}</h3>
            <p>
              {graphStats.elements} elements · {graphStats.customElements} custom · {graphStats.compounds} compounds · {graphStats.proteins} targets · {graphStats.medicines} medicines
            </p>
          </div>
        </aside>

        <main className="mol-main-stage mol-canvas-card">
          <div className="mol-stage-toolbar mol-canvas-toolbar">
            <div className="mol-toolgroup mol-library-buttons">
              <button type="button" onClick={() => setPicker({ type: "element", anchorNodeId: selectedNodeId })}>
                Element
              </button>
              <button type="button" onClick={() => setPicker({ type: "compound", anchorNodeId: selectedNodeId })}>
                Compound
              </button>
              <button type="button" onClick={() => setPicker({ type: "protein", anchorNodeId: selectedNodeId })}>
                Protein
              </button>
              <button type="button" onClick={() => setPicker({ type: "medicine", anchorNodeId: selectedNodeId })}>
                Medicine
              </button>
            </div>

            <div className="mol-toolgroup right mol-canvas-actions">
              <button type="button" onClick={autoLayout}>
                Layout
              </button>
              <button type="button" onClick={centerGraph}>
                Center
              </button>
              <button type="button" className="mol-icon-button" onClick={() => setRotation((r) => r - 15)}>
                ⟲
              </button>
              <button type="button" className="mol-icon-button" onClick={() => setRotation((r) => r + 15)}>
                ⟳
              </button>
              <button type="button" onClick={clearGraph}>
                Clear
              </button>
            </div>
          </div>

          <div className="mol-status-row mol-canvas-status">
            <strong>Graph canvas</strong>
            <span>{status}</span>
          </div>

          <svg
            ref={svgRef}
            className="mol-svg"
            viewBox={viewBox}
            onContextMenu={(event) => event.preventDefault()}
            onClick={() => {
              setContextMenu(null);
              setEdgeContextMenu(null);
              if (!dragRef.current) setStatus(linkSourceId ? "Click a target node to complete the link." : "Canvas ready.");
            }}
          >
            <defs>
              <pattern id="molGrid" width="32" height="32" patternUnits="userSpaceOnUse">
                <path d="M 32 0 L 0 0 0 32" fill="none" stroke="#e5e7eb" strokeWidth="1" />
              </pattern>

              <filter id="molShadow" x="-30%" y="-30%" width="160%" height="160%">
                <feDropShadow dx="0" dy="8" stdDeviation="8" floodColor="#0f172a" floodOpacity="0.12" />
              </filter>
            </defs>

            <rect width={CANVAS.width} height={CANVAS.height} fill="#ffffff" />
            <rect width={CANVAS.width} height={CANVAS.height} fill="url(#molGrid)" opacity="0.74" />

            <g ref={viewportRef}>
              <g transform={`rotate(${rotation} ${CANVAS.width / 2} ${CANVAS.height / 2})`}>
                {edges.map((edge) => {
                  const source = nodes.find((n) => n.id === edge.source);
                  const target = nodes.find((n) => n.id === edge.target);

                  if (!source || !target) return null;

                  const style = BOND_TYPES[edge.bondType] || BOND_TYPES.single;

                  const sx = Number(source.x || 0);
                  const sy = Number(source.y || 0);
                  const tx = Number(target.x || 0);
                  const ty = Number(target.y || 0);
                  const mx = (sx + tx) / 2;
                  const my = (sy + ty) / 2;

                  return (
                    <g key={edge.id} className="mol-edge" onContextMenu={(event) => openEdgeContextMenu(event, edge)}>
                      <line className="mol-edge-hitbox" x1={sx} y1={sy} x2={tx} y2={ty} stroke="transparent" strokeWidth="18" />
                      <line
                        className="mol-edge-line"
                        x1={sx}
                        y1={sy}
                        x2={tx}
                        y2={ty}
                        stroke={style.stroke}
                        strokeWidth={style.width}
                        strokeDasharray={style.dash}
                      />
                      <circle className="mol-edge-dot" cx={mx} cy={my} r="3" fill={style.stroke} />
                      <text className="mol-edge-label" x={mx + 7} y={my - 7}>
                        {style.label}
                      </text>
                    </g>
                  );
                })}

                {nodes.map((node) => {
                  const theme = NODE_THEME[node.type] || NODE_THEME.compound;
                  const width = nodeWidth(node);
                  const height = nodeHeight(node);
                  const x = Number(node.x || 0) - width / 2;
                  const y = Number(node.y || 0) - height / 2;
                  const selected = selectedNodeId === node.id;
                  const source = linkSourceId === node.id;

                  return (
                    <g
                      key={node.id}
                      className={`mol-node ${node.type} ${selected ? "selected" : ""} ${source ? "link-source bond-source" : ""}`}
                      transform={`translate(${x} ${y})`}
                      onPointerDown={(event) => startNodeDrag(event, node)}
                      onClick={(event) => handleNodeClick(event, node)}
                      onContextMenu={(event) => openNodeContextMenu(event, node)}
                      onDoubleClick={(event) => {
                        event.stopPropagation();
                        removeNode(node.id);
                      }}
                    >
                      <rect
                        width={width}
                        height={height}
                        rx="8"
                        fill={getNodeFill(node)}
                        stroke={selected ? "#f59e0b" : getNodeColor(node)}
                        strokeWidth={selected ? 2.6 : 1.6}
                        filter="url(#molShadow)"
                      />
                      <rect x="0" y="0" width="7" height={height} rx="4" fill={getNodeColor(node)} />
                      <text x="17" y="24" className="mol-node-title mol-node-label">
                        {truncate(node.label, node.type === "element" || node.type === "custom_element" ? 22 : 28)}
                      </text>
                      <text x="17" y="43" className="mol-node-subtitle mol-node-sub">
                        {truncate(node.subLabel || theme.label, node.type === "element" || node.type === "custom_element" ? 28 : 34)}
                      </text>
                      {(node.type === "compound" || node.type === "biomolecule") && (
                        <text x="17" y="63" className="mol-node-subtitle mol-node-sub">
                          Right-click: expand / link / remove bond
                        </text>
                      )}
                    </g>
                  );
                })}
              </g>
            </g>
          </svg>
        </main>

        <aside className="mol-sidebar mol-right">
          <div className="mol-card mol-actions-card">
            <div className="mol-card-title">Research actions</div>

            <button type="button" className="mol-primary-btn primary" onClick={askMedgen} disabled={isStreaming}>
              {isStreaming ? "Streaming..." : "Ask Medgen"}
            </button>

            <button type="button" onClick={saveCandidate}>
              Save candidate
            </button>

            <button type="button" onClick={addToAvatarQueue}>
              Add to avatar queue
            </button>

            <button type="button" onClick={() => saveFrontendSnapshot()}>
              Save avatar snapshot
            </button>

            <button type="button" className="mol-dark-btn dark" onClick={onRunAvatarSimulation}>
              Run avatar simulation
            </button>
          </div>

          <div className="mol-card mol-avatar-snapshot-card">
            <div className="mol-card-title">Local avatar snapshots</div>

            <input
              className="mol-save-input"
              value={snapshotName}
              onChange={(event) => setSnapshotName(event.target.value)}
              placeholder="Snapshot name..."
            />

            <button type="button" className="mol-primary-btn" onClick={() => saveFrontendSnapshot()}>
              Save current studio
            </button>

            <div className="mol-snapshot-list">
              {snapshots.length === 0 ? (
                <p>No frontend snapshots yet.</p>
              ) : (
                snapshots.map((snapshot) => (
                  <div key={snapshot.id} className="mol-snapshot-item">
                    <strong>{snapshot.name}</strong>
                    <span>
                      {new Date(snapshot.created_at).toLocaleString()} · {snapshot.graph_formula || "No formula"}
                    </span>
                    <div className="mol-snapshot-actions">
                      <button type="button" onClick={() => loadFrontendSnapshot(snapshot)}>
                        Load
                      </button>
                      <button type="button" className="mol-danger" onClick={() => deleteFrontendSnapshot(snapshot.id)}>
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="mol-card mol-dark-card mol-guidance-card">
            <div className="mol-card-title">Research workflow</div>
            <p>
              Add a biomolecule as a clean block first. Connect it to atoms, proteins, medicines, or another biomolecule. Remove wrong links from the linked-node panel, right-clicking an edge, or the links table.
            </p>
          </div>

          <div className="mol-card mol-medgen-card">
            <div className="mol-card-title">
              Medgen response {simulationId ? `#${simulationId}` : ""}
              {isStreaming && <span className="mol-live-badge">Live</span>}
            </div>

            {isStreaming && !medgenText && (
              <div className="mol-medgen-thinking">
                Medgen/Qwen is preparing the chemistry reasoning stream. For larger graphs, this can take a bit.
              </div>
            )}

            <pre>
              {medgenText ||
                (isStreaming
                  ? "Waiting for first reasoning tokens..."
                  : "No response yet. Ask Medgen after building the graph.")}
            </pre>

            {streamEvents.length > 0 && (
              <div className="mol-stream-events">
                {streamEvents.map((item) => (
                  <div key={item.id} className={`mol-stream-event ${item.type}`}>
                    <span>
                      <strong>{item.ts}</strong> · {item.message}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>
      </div>

      <div className="mol-data-panel mol-data-card">
        <div className="mol-data-head">
          <div>
            <strong>Graph data</strong>
            <span>Inspectable node/link table for the designed candidate.</span>
          </div>

          <div className="mol-data-tabs">
            <button type="button" className={tableMode === "nodes" ? "active" : ""} onClick={() => setTableMode("nodes")}>
              Nodes
            </button>
            <button type="button" className={tableMode === "edges" ? "active" : ""} onClick={() => setTableMode("edges")}>
              Links
            </button>
          </div>
        </div>

        {tableMode === "nodes" ? (
          <NodeTable nodes={nodes} onRemoveNode={removeNode} />
        ) : (
          <EdgeTable edges={edges} nodes={nodes} onRemoveEdge={removeEdge} onSetType={updateEdgeBond} />
        )}
      </div>

      {contextMenu && (
        <NodeContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          node={nodes.find((n) => n.id === contextMenu.nodeId)}
          bondType={bondType}
          setBondType={setBondType}
          onUseAsSource={() => {
            setLinkSourceId(contextMenu.nodeId);
            setContextMenu(null);
            setStatus("Link source selected. Click target node.");
          }}
          onExpandCompound={() => expandSelectedCompoundNode(contextMenu.nodeId)}
          onAdd={(type) => {
            setPicker({
              type,
              anchorNodeId: contextMenu.nodeId,
            });
            setContextMenu(null);
          }}
          onRemove={() => removeNode(contextMenu.nodeId)}
          onClose={() => setContextMenu(null)}
        />
      )}

      {edgeContextMenu && (
        <EdgeContextMenu
          x={edgeContextMenu.x}
          y={edgeContextMenu.y}
          edge={edges.find((e) => e.id === edgeContextMenu.edgeId)}
          onSetType={(type) => updateEdgeBond(edgeContextMenu.edgeId, type)}
          onRemove={() => removeEdge(edgeContextMenu.edgeId)}
          onClose={() => setEdgeContextMenu(null)}
        />
      )}

      {picker && (
        <PickerModal
          type={picker.type}
          search={pickerSearch}
          setSearch={setPickerSearch}
          elements={hydratedElements}
          compounds={compounds}
          proteins={proteins}
          medicines={medicines}
          onPick={(item, pickedType) => addNodeFromItem(pickedType || picker.type, item, picker.anchorNodeId)}
          onClose={() => setPicker(null)}
        />
      )}
    </section>
  );
}

function FormulaBuilder({ onApply, onWater, onGlucose }) {
  const [formula, setFormula] = useState("");

  return (
    <div className="mol-formula-card">
      <label className="mol-formula-label">Formula</label>

      <div className="mol-formula-row">
        <input
          className="mol-formula-input"
          value={formula}
          onChange={(e) => setFormula(e.target.value)}
          placeholder="H2O, NaCl, C6H12O6..."
        />
        <button type="button" className="mol-formula-apply" onClick={() => onApply(formula)}>
          Apply
        </button>
      </div>

      <div className="mol-formula-actions">
        <button type="button" onClick={onWater}>
          Water H2O
        </button>
        <button type="button" onClick={onGlucose}>
          Glucose
        </button>
      </div>
    </div>
  );
}

function NodeContextMenu({
  x,
  y,
  node,
  bondType,
  setBondType,
  onUseAsSource,
  onExpandCompound,
  onAdd,
  onRemove,
  onClose,
}) {
  if (!node) return null;

  const isCompoundBlock = node.type === "compound" || node.type === "biomolecule";

  return (
    <div className="mol-context-menu" style={{ left: x, top: y }} onClick={(event) => event.stopPropagation()}>
      <div className="mol-context-head">
        <strong>{node.label}</strong>
        <span>{NODE_THEME[node.type]?.label || node.type}</span>
      </div>

      {isCompoundBlock && (
        <div className="mol-context-section">
          <small>Compound block</small>
          <button type="button" className="mol-expand-btn" onClick={onExpandCompound}>
            Expand compound into atoms
          </button>
        </div>
      )}

      <div className="mol-context-section">
        <small>Bond/link type</small>

        <div className="mol-context-bonds">
          {Object.entries(BOND_TYPES).map(([key, item]) => (
            <button key={key} type="button" className={bondType === key ? "active" : ""} onClick={() => setBondType(key)}>
              {item.label}
            </button>
          ))}
        </div>

        <button type="button" className="primary" onClick={onUseAsSource}>
          Use as link source
        </button>
      </div>

      <div className="mol-context-section">
        <small>Add linked node</small>
        <button type="button" onClick={() => onAdd("element")}>
          Element
        </button>
        <button type="button" onClick={() => onAdd("compound")}>
          Compound / biomolecule
        </button>
        <button type="button" onClick={() => onAdd("protein")}>
          Protein / target
        </button>
        <button type="button" onClick={() => onAdd("medicine")}>
          Medicine / vaccine
        </button>
      </div>

      <div className="mol-context-section row">
        <button type="button" className="danger" onClick={onRemove}>
          Remove
        </button>
        <button type="button" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

function EdgeContextMenu({ x, y, edge, onSetType, onRemove, onClose }) {
  if (!edge) return null;

  return (
    <div className="mol-context-menu mol-edge-context-menu" style={{ left: x, top: y }} onClick={(event) => event.stopPropagation()}>
      <div className="mol-context-head">
        <strong>Bond / Link</strong>
        <span>{BOND_TYPES[edge.bondType]?.label || edge.bondType}</span>
      </div>

      <div className="mol-context-section">
        <small>Change type</small>

        <div className="mol-context-bonds">
          {Object.entries(BOND_TYPES).map(([key, item]) => (
            <button key={key} type="button" className={edge.bondType === key ? "active" : ""} onClick={() => onSetType(key)}>
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mol-context-section row">
        <button type="button" className="danger" onClick={onRemove}>
          Remove edge
        </button>
        <button type="button" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

function PickerModal({
  type,
  search,
  setSearch,
  elements,
  compounds,
  proteins,
  medicines,
  onPick,
  onClose,
}) {
  const [tab, setTab] = useState(type || "element");

  useEffect(() => {
    setTab(type || "element");
  }, [type]);

  const sources = {
    element: safeList(elements),
    compound: safeList(compounds),
    protein: safeList(proteins),
    medicine: safeList(medicines),
  };

  const items = sources[tab] || [];

  const filtered = items
    .filter((item) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return JSON.stringify(item).toLowerCase().includes(q);
    })
    .slice(0, 220);

  return (
    <div className="mol-picker-backdrop" onClick={onClose}>
      <div className="mol-picker-modal" onClick={(event) => event.stopPropagation()}>
        <div className="mol-picker-head">
          <div>
            <p>Studio library</p>
            <h2>Add graph node</h2>
            <span>Choose an element, compound, protein/target, medicine or vaccine candidate.</span>
          </div>

          <button type="button" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="mol-picker-tabs">
          {[
            ["element", "Elements"],
            ["compound", "Compounds"],
            ["protein", "Proteins / Targets"],
            ["medicine", "Medicines / Vaccines"],
          ].map(([key, label]) => (
            <button key={key} type="button" className={tab === key ? "active" : ""} aria-pressed={tab === key} onClick={() => setTab(key)}>
              {label}
            </button>
          ))}
        </div>

        <input
          className="mol-picker-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search ${tab}...`}
          autoFocus
        />

        <div className="mol-picker-grid">
          {filtered.map((item, index) => (
            <button key={item.id || item.symbol || `${tab}-${index}`} type="button" onClick={() => onPick(item, tab)}>
              <strong>{labelOf(item, tab)}</strong>
              <span>{subLabelOf(item, tab)}</span>
              <small>{tab === "compound" ? "Adds as block" : NODE_THEME[tab]?.label || tab}</small>
            </button>
          ))}

          {!filtered.length && <div className="mol-picker-empty">No matching records.</div>}
        </div>
      </div>
    </div>
  );
}

function NodeTable({ nodes, onRemoveNode }) {
  return (
    <div className="mol-table-wrap">
      <table className="mol-data-table">
        <thead>
          <tr>
            <th>Label</th>
            <th>Type</th>
            <th>P/N/E</th>
            <th>Context</th>
            <th>X</th>
            <th>Y</th>
            <th>Action</th>
          </tr>
        </thead>

        <tbody>
          {nodes.map((node) => (
            <tr key={node.id}>
              <td>{node.label}</td>
              <td>{NODE_THEME[node.type]?.label || node.type}</td>
              <td>
                {node.type === "element" || node.type === "custom_element"
                  ? `p${node.protons ?? "?"} n${node.neutrons ?? "?"} e${node.electrons ?? "?"}`
                  : "—"}
              </td>
              <td>{node.subLabel}</td>
              <td>{Number(node.x || 0).toFixed(1)}</td>
              <td>{Number(node.y || 0).toFixed(1)}</td>
              <td>
                <button className="mol-table-action danger" type="button" onClick={() => onRemoveNode(node.id)}>
                  Remove
                </button>
              </td>
            </tr>
          ))}

          {!nodes.length && (
            <tr>
              <td colSpan="7">No nodes yet.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function EdgeTable({ edges, nodes, onRemoveEdge, onSetType }) {
  const label = (id) => nodes.find((node) => node.id === id)?.label || id;

  return (
    <div className="mol-table-wrap">
      <table className="mol-data-table">
        <thead>
          <tr>
            <th>Source</th>
            <th>Target</th>
            <th>Type</th>
            <th>Change</th>
            <th>Action</th>
          </tr>
        </thead>

        <tbody>
          {edges.map((edge) => (
            <tr key={edge.id}>
              <td>{label(edge.source)}</td>
              <td>{label(edge.target)}</td>
              <td>{BOND_TYPES[edge.bondType]?.label || edge.bondType}</td>
              <td>
                <select
                  value={edge.bondType}
                  onChange={(event) => onSetType(edge.id, event.target.value)}
                  className="mol-save-input"
                >
                  {Object.entries(BOND_TYPES).map(([key, item]) => (
                    <option key={key} value={key}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <button className="mol-table-action danger" type="button" onClick={() => onRemoveEdge(edge.id)}>
                  Remove
                </button>
              </td>
            </tr>
          ))}

          {!edges.length && (
            <tr>
              <td colSpan="5">No links yet.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}